import { z } from 'zod';

import { IdSchema, OptionalPythonicNameSchema } from '../../base/primitives';
import {
  DelayResponseDurationSchema,
  DelayResponseMessageSchema,
  DelayResponseReferencesSchema,
  FunctionArchivedSchema,
  FunctionCodeSchema,
  FunctionDescriptionSchema,
  FunctionErrorLinenoSchema,
  FunctionErrorMessageSchema,
  FunctionErrorTextSchema,
  FunctionNameSchema,
  FunctionParameterTypeSchema,
  LatencyControlEnabledSchema,
  LatencyControlInitialDelaySchema,
  LatencyControlIntervalSchema,
  OptionalFunctionParameterDescriptionSchema,
} from './function.schema';

/**
 * Form schema for function parameters
 * Simplified version for UI forms with array structure
 * Description is optional with default in forms (required in base schema)
 */
export const FunctionParameterFormSchema = z.object({
  id: IdSchema,
  name: OptionalPythonicNameSchema,
  type: FunctionParameterTypeSchema,
  description:
    OptionalFunctionParameterDescriptionSchema.optional().default(''),
});

export type FunctionParameterForm = z.infer<typeof FunctionParameterFormSchema>;

/**
 * Form schema for function errors
 * Represents syntax or runtime errors in the function code
 */
export const FunctionErrorFormSchema = z.object({
  message: FunctionErrorMessageSchema,
  lineno: FunctionErrorLinenoSchema,
  text: FunctionErrorTextSchema,
});

export type FunctionErrorForm = z.infer<typeof FunctionErrorFormSchema>;

/**
 * Form schema for delay responses
 * Messages to play while waiting for function execution
 */
export const DelayResponseFormSchema = z.object({
  id: IdSchema,
  duration: DelayResponseDurationSchema,
  message: DelayResponseMessageSchema,
  references: DelayResponseReferencesSchema.optional(),
});

export type DelayResponseForm = z.infer<typeof DelayResponseFormSchema>;

/**
 * Form schema for latency control
 * Configuration for handling slow function execution with defaults
 */
export const FunctionLatencyControlFormSchema = z.object({
  enabled: LatencyControlEnabledSchema.default(false),
  initialDelay: LatencyControlInitialDelaySchema,
  interval: LatencyControlIntervalSchema,
  delayResponses: z.array(DelayResponseFormSchema),
});

export type FunctionLatencyControlForm = z.infer<
  typeof FunctionLatencyControlFormSchema
>;

/**
 * Form schema for creating/editing functions
 * Simplified version for UI forms with defaults for optional fields
 * Description is optional with default in forms (required in base schema)
 */
export const FunctionFormSchema = z.object({
  id: IdSchema,
  name: FunctionNameSchema,
  description: FunctionDescriptionSchema.default(''),
  code: FunctionCodeSchema,
  parameters: z.array(FunctionParameterFormSchema).optional(),
  errors: z.array(FunctionErrorFormSchema),
  archived: FunctionArchivedSchema.optional().default(false),
});

export type FunctionForm = z.infer<typeof FunctionFormSchema>;
